<script setup lang="ts">
import { computed } from 'vue';
import {
  addressAsText,
  booleanAsText,
  formatDate,
  formatFullFormOfAddress,
  formatPhoneNumber,
  formatPriceWithCurrency,
} from '@/composables/formatter';
import useBookingStore from '@/store/booking';
import useInitStore from '@/store/init';
import useOrderStore from '@/store/order';
import type { PhoneNumber } from '@/types/common';
import { BookingMode } from '@/types/order-main';
import { ConnectionType } from '@/types/step-data';

interface SummaryDataEntry {
  label: string;
  model: string;
}

interface SummaryData {
  title: string;
  data: SummaryDataEntry[];
}

const bookingStore = useBookingStore();
const initStore = useInitStore();
const orderStore = useOrderStore();

function mapConnectionType(connectionType: ConnectionType): string {
  return {
    [ConnectionType.NEW_CONNECTION]: 'Neuanschluss',
    [ConnectionType.SWITCHING_PROVIDER]: 'Anbieterwechsel',
    [ConnectionType.NONE]: 'keine Angabe',
  }[connectionType];
}

function getSelectionValue(name: string) {
  return orderStore.productData?.selections.find(item => item.name === name)?.article.value
    ?? 'Unbekannt';
}

function checkAndFormatPhoneNumber(phoneNumber?: PhoneNumber | null): string {
  if (!phoneNumber) {
    return 'nicht angegeben';
  }
  if (!phoneNumber.prefix || !phoneNumber.number) {
    return 'nicht angegeben';
  }
  if (phoneNumber.prefix === 'null' || phoneNumber.number === 'null') {
    return 'nicht angegeben';
  }
  return formatPhoneNumber(phoneNumber);
}

const orderRequestData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.orderRequestData) {
    return [];
  }
  const {
    connectionPoint,
    bookingWithProduct,
    price,
    wantsBankTransfer,
  } = orderStore.orderRequestData;

  return [
    {
      label: 'Adresse des Anschlusses',
      model: initStore.paramsAddressText!,
    },
    {
      label: 'FTTH-Hausanschluss beauftragen',
      model: booleanAsText(bookingStore.isCurrentBookingModeAnyHouseConnection),
    },
    {
      label: 'Glasfaserprodukt direkt im Anschluss mit beauftragen',
      model: booleanAsText(bookingWithProduct),
    },
    {
      label: 'Einmalige Hausanschlusskosten',
      model: formatPriceWithCurrency(price),
    },
    {
      label: 'Kunde wünscht Zahlung per Überweisung',
      model: booleanAsText(wantsBankTransfer),
    },
    ...(connectionPoint ? [
      {
        label: 'Wo befindet sich der Hausanschluss',
        model: connectionPoint,
      },
    ] : []),
  ];
});

const ordererData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.ordererData) {
    return [];
  }

  const {
    additionalInfo,
    contact,
    household,
    invoiceAddress,
    isOwnerOrManager,
  } = orderStore.ordererData;

  return [
    {
      label: 'Name',
      model: formatFullFormOfAddress(household!),
    },
    {
      label: 'Rechnungsadresse',
      model: invoiceAddress ? addressAsText(invoiceAddress) : 'Keine Auswahl',
    },
    {
      label: 'Geburtstag',
      model: household?.dateOfBirth ? formatDate(household.dateOfBirth)! : 'Keine Auswahl',
    },
    {
      label: 'Telefonnummer',
      model: checkAndFormatPhoneNumber(contact.tel),
    },
    {
      label: 'Mobilfunk-Nummer',
      model: checkAndFormatPhoneNumber(contact.mobile),
    },
    {
      label: 'E-Mail Adresse',
      model: contact?.email ?? 'Keine Auswahl',
    },
    {
      label: 'Auftraggeber ist Eigentümer des Anschlussobjektes',
      model: booleanAsText(isOwnerOrManager === '1'),
    },
    {
      label: 'Auftraggeber ist Verwalter des Anschlussobjektes',
      model: booleanAsText(isOwnerOrManager === '2'),
    },
    ...(additionalInfo ? [
      {
        label: 'Ergänzende Informationen zum Auftrag',
        model: additionalInfo,
      },
    ] : []),
  ];
});

const mphData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.mphData) {
    return [];
  }

  return [
    {
      label: 'Gebäude im Bau',
      model: booleanAsText(orderStore.mphData.buildingUnderConstruction),
    },
    {
      label: 'Eigentümergemeinschaft',
      model: booleanAsText(orderStore.mphData.isCommunityOfOwners),
    },
    {
      label: 'Anzahl Eigentümer',
      model: orderStore.mphData.numberOfOwners.toString(),
    },
    {
      label: 'Anzahl Einheiten',
      model: orderStore.mphTotalUnits.toString(),
    },
    {
      label: 'Gesamtpreis',
      model: formatPriceWithCurrency(orderStore.mphData.ne4ExtensionDiscountedPrice),
    },
  ];
});

const productData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.productData) {
    return [];
  }

  const result: SummaryDataEntry[] = [
    {
      label: 'Produkt',
      model: getSelectionValue('selectedProduct'),
    },
    {
      label: 'Festnetzoption',
      model: getSelectionValue('selectedLandlineOption'),
    },
    {
      label: 'Sicherheitspaket',
      model: getSelectionValue('selectedSecurityPackage'),
    },
    {
      label: 'Hardware',
      model: getSelectionValue('selectedHardware'),
    },
    {
      label: 'Auslandsoption',
      model: getSelectionValue('selectedAbroadOption'),
    },
    {
      label: 'TV-Option',
      model: getSelectionValue('selectedTVOption'),
    },
  ];

  const recordingMemory = getSelectionValue('recordingMemory');
  if (recordingMemory !== 'Unbekannt') {
    result.push({
      label: 'Aufnahmespeicher',
      model: recordingMemory,
    });
  }

  result.push({
    label: 'Vertragslaufzeit',
    model: getSelectionValue('invoiceDelivery'),
  });
  result.push({
    label: 'Aktionscode',
    model: orderStore.productData.appliedActionCode || 'Kein Aktionscode angewendet',
  });

  return result;
});

const customerData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.customerData) {
    return [];
  }

  const {
    connectionType,
    creditCheck,
    customerPassword,
    dataUsage,
    itemizedBill,
    phoneBookEntry,
    wantsBankTransfer,
  } = orderStore.customerData;

  return [
    {
      label: 'Anschlusstyp',
      model: mapConnectionType(connectionType!),
    },
    {
      label: 'Kunde wünscht Überweisung',
      model: booleanAsText(wantsBankTransfer),
    },
    {
      label: 'Kundenkennwort',
      model: customerPassword,
    },
    {
      label: 'Bonitätsprüfung',
      model: creditCheck ? 'Einverstanden' : 'Nicht Einverstanden',
    },
    {
      label: 'Bestandsdatennutzung',
      model: dataUsage ? 'Einverstanden' : 'Nicht Einverstanden',
    },
    {
      label: 'Einzelverbindungsnachweis',
      model: itemizedBill,
    },
    {
      label: 'Telefonbucheintrag',
      model: phoneBookEntry,
    },
  ];
});

const ownerData = computed<SummaryDataEntry[]>(() => {
  if (!orderStore.ownerData) {
    return [];
  }

  const {
    isOwner,
    household,
    address,
  } = orderStore.ownerData;
  return [
    {
      label: 'Typ',
      model: isOwner ? 'Eigentümer' : 'Verwalter',
    },
    {
      label: 'Name',
      model: household.isCompany
        ? household.companyName!
        : formatFullFormOfAddress(household).trim(),
    },
    {
      label: 'Adresse',
      model: address ? addressAsText(address) : 'Keine Auswahl',
    },
  ];
});

const summary = computed<SummaryData[]>(() => {
  const result: SummaryData[] = [];

  if (!bookingStore.isCurrentBookingModeAnyProductOnly) {
    result.push({
      title: 'Auftragsanfrage',
      data: orderRequestData.value,
    });
  }

  result.push({
    title: 'Auftraggeberdaten',
    data: ordererData.value,
  });

  if (
    orderStore.orderRequestData?.bookingWithProduct
      && orderStore.productData
      && orderStore.customerData
  ) {
    result.push(...[
      {
        title: 'Produktkonfiguration',
        data: productData.value,
      },
      {
        title: 'Kundendaten',
        data: customerData.value,
      },
    ]);
  }

  if (orderStore.orderRequestData?.isMphOrder && orderStore.mphData) {
    result.push({
      title: 'Gebäude-Glasfaser-Netz',
      data: mphData.value,
    });
  }

  if (bookingStore.currentBookingMode === BookingMode.productOnlyMph && orderStore.ownerData) {
    result.push({
      title: 'Eigentümerinformationen',
      data: ownerData.value,
    });
  }

  return result;
});
</script>

<template>
  <v-form ref="stepSummaryRef">
    <div class="d-flex flex-wrap">
      <v-col
        v-for="card in summary"
        :key="card.title"
        cols="12"
        md="6"
        lg="4"
      >
        <v-alert class="h-full align-content-start info-box" :icon="false">
          <template #title>
            <h4 class="mb-4">
              {{ card.title }}
            </h4>
          </template>
          <v-container class="pa-0">
            <v-text-field
              v-for="field in card.data"
              :key="field.label"
              v-model="field.model"
              :label="field.label"
              class="text-grey-darken-3"
              variant="underlined"
              density="compact"
              readonly
            />
          </v-container>
        </v-alert>
      </v-col>
    </div>
  </v-form>
</template>

<style lang="scss">
  @use '../assets/sass/main';
</style>
